import {
  Circle,
  Document,
  Font,
  Image,
  Page,
  Path,
  StyleSheet,
  Svg,
  Text,
  View
} from '@react-pdf/renderer'

import type {
  JourneyAnswerExportChallenge,
  JourneyAnswerExportRecord
} from '../types/answer-export'

Font.registerHyphenationCallback(word => [word])

const c = {
  primary: '#244bcd',
  primaryDark: '#00298f',
  accent: '#8f85f4',
  body: '#434368',
  strong: '#1d1d2a',
  muted: '#6b7280',
  faint: '#9ca3af',
  surface: '#f4f6f9',
  cardBorder: '#e5e7eb',
  success: '#28bd50',
  successDark: '#1b6c40',
  successBg: '#e7f7ee',
  danger: '#dc2626',
  dangerDark: '#b91c1c',
  dangerBg: '#fdecec',
  lavender: '#eef2ff',
  neutralBg: '#f3f4f6',
  white: '#ffffff'
}

// Levels with up to this many challenges are kept on a single page (moved to a
// new page if they don't fit). Larger levels wrap normally to avoid overlap.
const LEVEL_KEEP_TOGETHER_MAX = 5
// Space (pt) a wrapping level header reserves ahead so it never sits orphaned
// at the bottom of a page.
const ORPHAN_GUARD = 200

type ChallengeStatus = 'correct' | 'incorrect' | 'open' | 'unanswered'

// Reflection questions have no answer key, so they are never compared against
// one: only the student's own answer is shown. Any correctness the payload may
// carry for them is ignored on purpose.
function isReflectionChallenge(challenge: JourneyAnswerExportChallenge) {
  return challenge.answer_type === 'personal'
}

function statusOf(challenge: JourneyAnswerExportChallenge): ChallengeStatus {
  if (isReflectionChallenge(challenge)) {
    return challenge.answer_value ? 'open' : 'unanswered'
  }
  if (challenge.is_correct === true) return 'correct'
  if (challenge.is_correct === false) return 'incorrect'
  return challenge.answer_value ? 'open' : 'unanswered'
}

/* ------------------------------- SVG icons ------------------------------- */

function CalendarIcon({ color = c.muted }: { color?: string }) {
  return (
    <Svg width={12} height={12} viewBox="0 0 24 24">
      <Path
        d="M7 2v3M17 2v3M3 8h18M4 5h16a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z"
        stroke={color}
        strokeWidth={1.7}
        fill="none"
      />
    </Svg>
  )
}

function FlagIcon({ color = c.accent }: { color?: string }) {
  return (
    <Svg width={13} height={13} viewBox="0 0 24 24">
      <Path
        d="M5 21V4M5 4h11l-2 4 2 4H5"
        stroke={color}
        strokeWidth={1.9}
        fill="none"
      />
    </Svg>
  )
}

function PersonIcon({ color = c.muted }: { color?: string }) {
  return (
    <Svg width={16} height={16} viewBox="0 0 24 24">
      <Circle cx={12} cy={8} r={4} stroke={color} strokeWidth={1.7} fill="none" />
      <Path
        d="M4 21c0-4 4-6 8-6s8 2 8 6"
        stroke={color}
        strokeWidth={1.7}
        fill="none"
      />
    </Svg>
  )
}

function QuizIcon({ color }: { color: string }) {
  return (
    <Svg width={18} height={18} viewBox="0 0 24 24">
      <Path
        d="M8 4h8a1 1 0 0 1 1 1v15a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Z"
        stroke={color}
        strokeWidth={1.7}
        fill="none"
      />
      <Path d="M9 3h6v3H9z" stroke={color} strokeWidth={1.7} fill="none" />
      <Path
        d="M9.5 12.5l1.5 1.5 3-3.5"
        stroke={color}
        strokeWidth={1.7}
        fill="none"
      />
    </Svg>
  )
}

function PencilIcon({ color }: { color: string }) {
  return (
    <Svg width={18} height={18} viewBox="0 0 24 24">
      <Path
        d="M15 4l5 5L9 20H4v-5L15 4Z"
        stroke={color}
        strokeWidth={1.7}
        fill="none"
      />
    </Svg>
  )
}

function CheckCircleIcon({ color }: { color: string }) {
  return (
    <Svg width={11} height={11} viewBox="0 0 24 24">
      <Circle cx={12} cy={12} r={10} fill={color} />
      <Path
        d="M7.5 12.5l3 3 6-7"
        stroke={c.white}
        strokeWidth={2.2}
        fill="none"
      />
    </Svg>
  )
}

function XCircleIcon({ color }: { color: string }) {
  return (
    <Svg width={11} height={11} viewBox="0 0 24 24">
      <Circle cx={12} cy={12} r={10} fill={color} />
      <Path
        d="M8.5 8.5l7 7M15.5 8.5l-7 7"
        stroke={c.white}
        strokeWidth={2.2}
        fill="none"
      />
    </Svg>
  )
}

function MinusCircleIcon({ color }: { color: string }) {
  return (
    <Svg width={11} height={11} viewBox="0 0 24 24">
      <Circle cx={12} cy={12} r={10} fill={color} />
      <Path d="M7.5 12h9" stroke={c.white} strokeWidth={2.2} fill="none" />
    </Svg>
  )
}

function BulbIcon({ color = c.white }: { color?: string }) {
  return (
    <Svg width={14} height={14} viewBox="0 0 24 24">
      <Path
        d="M9 18h6M10 21h4M12 3a6 6 0 0 1 4 10.5c-.7.7-1 1.3-1 2.5H9c0-1.2-.3-1.8-1-2.5A6 6 0 0 1 12 3Z"
        stroke={color}
        strokeWidth={1.7}
        fill="none"
      />
    </Svg>
  )
}

function ProgressRing({ pct }: { pct: number }) {
  const size = 54
  const stroke = 6
  const r = (size - stroke) / 2
  const circ = 2 * Math.PI * r
  const filled = (circ * Math.min(Math.max(pct, 0), 100)) / 100

  return (
    <View style={{ width: size, height: size, position: 'relative' }}>
      <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke={c.cardBorder}
          strokeWidth={stroke}
          fill="none"
        />
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke={c.success}
          strokeWidth={stroke}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={`${filled} ${circ}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </Svg>
      <View
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: size,
          height: size,
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Text style={{ fontSize: 12, fontFamily: 'Helvetica-Bold', color: c.successDark }}>
          {pct}%
        </Text>
      </View>
    </View>
  )
}

/* --------------------------------- styles -------------------------------- */

const styles = StyleSheet.create({
  page: {
    paddingTop: 24,
    paddingHorizontal: 28,
    paddingBottom: 40,
    fontSize: 11,
    color: c.body,
    lineHeight: 1.4
  },
  topbar: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16
  },
  brandRow: { flexDirection: 'row', alignItems: 'center' },
  brandMark: { width: 26, height: 26, marginRight: 8 },
  brandName: { fontFamily: 'Helvetica-Bold', fontSize: 13, color: c.primaryDark },
  brandTag: { fontSize: 7, color: c.muted, letterSpacing: 0.5 },
  topMeta: { flexDirection: 'row', alignItems: 'center' },
  topMetaItem: { flexDirection: 'row', alignItems: 'center', marginLeft: 16 },
  topMetaLabel: { fontSize: 7, color: c.muted },
  topMetaValue: { fontSize: 8.5, color: c.strong, fontFamily: 'Helvetica-Bold' },

  hero: {
    backgroundColor: c.surface,
    borderRadius: 12,
    padding: 18,
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  heroLabel: {
    fontSize: 8,
    letterSpacing: 1,
    color: c.primary,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 4
  },
  heroTitle: { fontSize: 21, fontFamily: 'Helvetica-Bold', color: c.primaryDark, lineHeight: 1.2 },
  studentRow: { flexDirection: 'row', alignItems: 'center', marginTop: 14 },
  studentAvatar: {
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: '#e5e7eb',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 8
  },
  studentLabel: { fontSize: 8, color: c.muted },
  studentName: { fontSize: 12, fontFamily: 'Helvetica-Bold', color: c.strong },

  progressCard: {
    backgroundColor: c.white,
    borderWidth: 1,
    borderColor: c.cardBorder,
    borderRadius: 10,
    padding: 12,
    flexDirection: 'row',
    alignItems: 'center',
    minWidth: 190
  },
  progressLabel: { fontSize: 7.5, letterSpacing: 0.6, color: c.muted, marginBottom: 3 },
  progressBig: { fontSize: 14, fontFamily: 'Helvetica-Bold', color: c.strong },
  progressSub: { fontSize: 8.5, color: c.muted },

  levelHeader: { flexDirection: 'row', alignItems: 'center', marginTop: 20, marginBottom: 10 },
  levelTitle: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    color: c.primaryDark,
    letterSpacing: 0.5,
    marginLeft: 7,
    marginRight: 10
  },
  levelRule: { flex: 1, height: 1, backgroundColor: c.cardBorder },

  card: {
    flexDirection: 'row',
    backgroundColor: c.white,
    borderWidth: 1,
    borderColor: c.cardBorder,
    borderRadius: 10,
    padding: 12,
    marginBottom: 10
  },
  cardLeft: { width: 58, alignItems: 'center' },
  numBadge: {
    width: 22,
    height: 22,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 6
  },
  numText: { fontSize: 10, fontFamily: 'Helvetica-Bold', color: c.white },
  typeLabel: { fontSize: 6.5, color: c.muted, letterSpacing: 0.5, marginTop: 3 },
  cardRight: { flex: 1, paddingLeft: 4 },
  cardTopRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start'
  },
  challengeTitle: {
    flex: 1,
    fontSize: 11.5,
    fontFamily: 'Helvetica-Bold',
    color: c.strong,
    paddingRight: 8
  },
  question: { fontSize: 9.5, color: c.muted, marginTop: 3 },

  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 10,
    paddingVertical: 3,
    paddingHorizontal: 7
  },
  statusText: { fontSize: 8, fontFamily: 'Helvetica-Bold', marginLeft: 4, letterSpacing: 0.3 },

  answerBox: { marginTop: 8, borderRadius: 6, padding: 8 },
  answerLabel: { fontSize: 7, letterSpacing: 0.6, fontFamily: 'Helvetica-Bold', marginBottom: 2 },
  answerValue: { fontSize: 10.5, fontFamily: 'Helvetica-Bold', color: c.strong },

  about: {
    flexDirection: 'row',
    backgroundColor: c.lavender,
    borderRadius: 10,
    padding: 12,
    marginTop: 16
  },
  aboutIcon: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: c.accent,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10
  },
  aboutTitle: { fontSize: 9.5, fontFamily: 'Helvetica-Bold', color: c.primaryDark, marginBottom: 2 },
  aboutText: { fontSize: 8.5, color: c.body },
  aboutNote: { fontSize: 7.5, color: c.faint, marginTop: 4 },

  footer: {
    position: 'absolute',
    bottom: 18,
    left: 28,
    right: 28,
    textAlign: 'center',
    fontSize: 7.5,
    color: c.faint
  }
})

const statusTheme: Record<
  ChallengeStatus,
  { circle: string; badgeBg: string; badgeText: string; answerBg: string; answerLabel: string }
> = {
  correct: {
    circle: c.success,
    badgeBg: c.successBg,
    badgeText: c.successDark,
    answerBg: c.successBg,
    answerLabel: c.successDark
  },
  incorrect: {
    circle: c.danger,
    badgeBg: c.dangerBg,
    badgeText: c.dangerDark,
    answerBg: c.dangerBg,
    answerLabel: c.dangerDark
  },
  open: {
    circle: c.accent,
    badgeBg: c.lavender,
    badgeText: c.primary,
    answerBg: c.lavender,
    answerLabel: c.primary
  },
  unanswered: {
    circle: c.faint,
    badgeBg: c.neutralBg,
    badgeText: c.muted,
    answerBg: c.neutralBg,
    answerLabel: c.muted
  }
}

function StatusBadge({ status }: { status: ChallengeStatus }) {
  const theme = statusTheme[status]

  if (status === 'open') return null

  const config = {
    correct: { icon: <CheckCircleIcon color={c.success} />, label: 'CORRETA' },
    incorrect: { icon: <XCircleIcon color={c.danger} />, label: 'INCORRETA' },
    unanswered: { icon: <MinusCircleIcon color={c.faint} />, label: 'NÃO RESPONDIDA' }
  }[status]

  return (
    <View style={[styles.statusBadge, { backgroundColor: theme.badgeBg }]}>
      {config.icon}
      <Text style={[styles.statusText, { color: theme.badgeText }]}>{config.label}</Text>
    </View>
  )
}

type JourneyAnswersPdfDocumentProps = {
  data: JourneyAnswerExportRecord
  // Overridable for tests/previews; in-app it resolves from the public folder.
  logoSrc?: string
}

export function JourneyAnswersPdfDocument({
  data,
  logoSrc = '/emails/logoMark.png'
}: JourneyAnswersPdfDocumentProps) {
  const scopeReportLabel =
    data.scope === 'level' ? 'RELATÓRIO DE NÍVEL' : 'RELATÓRIO DE JORNADA'
  const generatedAt = new Date().toLocaleDateString('pt-BR')

  const allChallenges = data.levels.flatMap(level => level.challenges)
  const total = allChallenges.length
  const done = allChallenges.filter(challenge => challenge.answer_value).length
  const pct = total > 0 ? Math.round((done / total) * 100) : 0

  let counter = 0

  return (
    <Document
      title={`Relatório de Respostas — ${data.journey.title}`}
      author="Missão Futuro"
      subject={`Respostas de ${data.student.name}`}
      creator="Missão Futuro"
    >
      <Page size="A4" style={styles.page}>
        <View style={styles.topbar}>
          <View style={styles.brandRow}>
            <Image style={styles.brandMark} src={logoSrc} />
            <View>
              <Text style={styles.brandName}>Missão Futuro</Text>
              <Text style={styles.brandTag}>EDUCAÇÃO EMPREENDEDORA</Text>
            </View>
          </View>
          <View style={styles.topMeta}>
            <View style={styles.topMetaItem}>
              <CalendarIcon />
              <View style={{ marginLeft: 5 }}>
                <Text style={styles.topMetaLabel}>Data de emissão</Text>
                <Text style={styles.topMetaValue}>{generatedAt}</Text>
              </View>
            </View>
          </View>
        </View>

        <View style={styles.hero}>
          <View style={{ flex: 1, paddingRight: 12 }}>
            <Text style={styles.heroLabel}>{scopeReportLabel}</Text>
            <Text style={styles.heroTitle}>{data.journey.title}</Text>
            <View style={styles.studentRow}>
              <View style={styles.studentAvatar}>
                <PersonIcon />
              </View>
              <View>
                <Text style={styles.studentLabel}>Aluno</Text>
                <Text style={styles.studentName}>{data.student.name}</Text>
              </View>
            </View>
          </View>

          <View style={styles.progressCard}>
            <View style={{ flex: 1, paddingRight: 10 }}>
              <Text style={styles.progressLabel}>PROGRESSO GERAL</Text>
              <Text style={styles.progressBig}>
                {done} de {total}
              </Text>
              <Text style={styles.progressSub}>atividades concluídas</Text>
            </View>
            <ProgressRing pct={pct} />
          </View>
        </View>

        {data.levels.map(level => {
          // Keep short levels whole (moves the block to a new page if it does
          // not fit). Long levels must wrap to avoid overlapping/clipped
          // content, so we only guard the header from being orphaned.
          const keepTogether = level.challenges.length <= LEVEL_KEEP_TOGETHER_MAX

          return (
            <View key={level.level_id} wrap={!keepTogether}>
              <View
                style={styles.levelHeader}
                minPresenceAhead={keepTogether ? undefined : ORPHAN_GUARD}
              >
                <FlagIcon />
                <Text style={styles.levelTitle}>{level.level_title.toUpperCase()}</Text>
                <View style={styles.levelRule} />
              </View>

              {level.challenges.map(challenge => {
              counter += 1
              const status = statusOf(challenge)
              const theme = statusTheme[status]
              const isReflection = isReflectionChallenge(challenge)

              return (
                <View key={challenge.challenge_id} style={styles.card} wrap={false}>
                  <View style={styles.cardLeft}>
                    <View style={[styles.numBadge, { backgroundColor: theme.circle }]}>
                      <Text style={styles.numText}>{counter}</Text>
                    </View>
                    {isReflection ? (
                      <PencilIcon color={c.accent} />
                    ) : (
                      <QuizIcon color={theme.circle} />
                    )}
                    <Text style={styles.typeLabel}>
                      {isReflection ? 'REFLEXÃO' : 'QUIZ'}
                    </Text>
                  </View>

                  <View style={styles.cardRight}>
                    <View style={styles.cardTopRow}>
                      <Text style={styles.challengeTitle}>{challenge.challenge_title}</Text>
                      <StatusBadge status={status} />
                    </View>
                    {challenge.question_text ? (
                      <Text style={styles.question}>{challenge.question_text}</Text>
                    ) : null}
                    <View style={[styles.answerBox, { backgroundColor: theme.answerBg }]}>
                      <Text style={[styles.answerLabel, { color: theme.answerLabel }]}>
                        RESPOSTA
                      </Text>
                      <Text style={styles.answerValue}>{challenge.answer_value || '-'}</Text>
                    </View>
                  </View>
                </View>
                )
              })}
            </View>
          )
        })}

        <View style={styles.about}>
          <View style={styles.aboutIcon}>
            <BulbIcon />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.aboutTitle}>Sobre este relatório</Text>
            <Text style={styles.aboutText}>
              Este relatório apresenta o desempenho do aluno nas atividades da jornada. Use-o
              como apoio para acompanhar a evolução do aprendizado e orientar os próximos passos.
            </Text>
            <Text style={styles.aboutNote}>
              O gabarito oficial não é exibido neste relatório.
            </Text>
          </View>
        </View>

        <Text style={styles.footer} fixed>
          Missão Futuro · Relatório de Respostas · Este documento não exibe o gabarito oficial
        </Text>
      </Page>
    </Document>
  )
}
